import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Zap, Search, Loader2, CreditCard, Gift } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { apiClient } from '../../lib/api-client';
import { Badge } from '../ui/badge';

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  subscription?: {
    plan: string;
    status: string;
    expiresAt: string;
  };
}

interface Plan {
  _id: string;
  name: string;
  slug: string;
  price: number;
  duration: number;
  description: string;
  isActive: boolean;
}

export function AdminRecharge() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<'email' | 'phone'>('email');
  const [searching, setSearching] = useState(false);
  const [recharging, setRecharging] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [loadingPlans, setLoadingPlans] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoadingPlans(true);
      const response = await apiClient.get('/plans?admin=true');
      const activePlans = response.data.data.plans.filter((p: Plan) => p.isActive);
      setPlans(activePlans);
    } catch (error: any) {
      toast.error('Failed to fetch plans');
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter search query');
      return;
    }

    try {
      setSearching(true);
      const response = await apiClient.get(`/admin/users?${searchType}=${searchQuery}`);
      const users = response.data.data.users || [];
      
      if (users.length > 0) {
        setUser(users[0]);
        toast.success('User found');
      } else {
        setUser(null);
        toast.error('User not found');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to search user');
      setUser(null);
    } finally {
      setSearching(false);
    }
  };

  const handleRecharge = async () => {
    if (!user || !selectedPlan) {
      toast.error('Please select a user and plan');
      return;
    }

    try {
      setRecharging(true);
      await apiClient.post('/admin/recharge/plan', {
        userId: user._id,
        planId: selectedPlan
      });
      
      toast.success('Plan recharged successfully!');
      
      // Refresh user data
      handleSearch();
      setSelectedPlan('');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to recharge plan');
    } finally {
      setRecharging(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Zap className="size-8 text-orange-600" />
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Admin Recharge</h1>
          <p className="text-gray-500">Give free plans to users</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Search User</CardTitle>
          <CardDescription>Find user by email or phone number</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Search By</Label>
              <Select value={searchType} onValueChange={(value: 'email' | 'phone') => setSearchType(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="phone">Phone</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>{searchType === 'email' ? 'Email Address' : 'Phone Number'}</Label>
              <div className="flex gap-2">
                <Input
                  placeholder={searchType === 'email' ? 'user@example.com' : '9876543210'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch} disabled={searching}>
                  {searching ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Search className="size-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>

          {user && (
            <div className="border rounded-lg p-4 bg-blue-50">
              <h3 className="font-semibold mb-2">User Found</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-600">Name:</span>
                  <span className="ml-2 font-medium">{user.firstName} {user.lastName}</span>
                </div>
                <div>
                  <span className="text-gray-600">Email:</span>
                  <span className="ml-2 font-medium">{user.email}</span>
                </div>
                <div>
                  <span className="text-gray-600">Phone:</span>
                  <span className="ml-2 font-medium">{user.phone}</span>
                </div>
                <div>
                  <span className="text-gray-600">Current Plan:</span>
                  {user.subscription?.plan ? (
                    <Badge className="ml-2" variant={user.subscription.status === 'active' ? 'default' : 'secondary'}>
                      {user.subscription.plan}
                    </Badge>
                  ) : (
                    <span className="ml-2 text-gray-500">No Plan</span>
                  )}
                </div>
                {user.subscription?.expiresAt && (
                  <div>
                    <span className="text-gray-600">Expires:</span>
                    <span className="ml-2 font-medium">{formatDate(user.subscription.expiresAt)}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {user && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gift className="size-5" />
              Recharge Plan
            </CardTitle>
            <CardDescription>Select a plan to give to the user for free</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Select Plan</Label>
              {loadingPlans ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="size-6 animate-spin text-gray-400" />
                </div>
              ) : (
                <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.map((plan) => (
                      <SelectItem key={plan._id} value={plan._id}>
                        {plan.name} - ₹{plan.price} ({plan.duration} days)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {selectedPlan && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <CreditCard className="size-5 text-green-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-green-900">Ready to Recharge</p>
                    <p className="text-sm text-green-700 mt-1">
                      The selected plan will be added to {user.firstName} account immediately and for free.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <Button 
              onClick={handleRecharge} 
              disabled={!selectedPlan || recharging}
              className="w-full"
            >
              {recharging ? (
                <>
                  <Loader2 className="size-4 mr-2 animate-spin" />
                  Recharging...
                </>
              ) : (
                <>
                  <Zap className="size-4 mr-2" />
                  Recharge Plan
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default AdminRecharge;
